import PropTypes from "prop-types";
import { Link as RouterLink } from "react-router-dom";
import { Box, Breadcrumbs, Link, Stack, Typography } from "@mui/material";
import { IconChevronRight } from "@tabler/icons";

// The top of every CMS page, as on Cases: where you are (breadcrumbs, when the
// page sits under another), the page title, one line saying what the page is
// for, and the page's actions on the right (stacked under the title on a
// phone). One primary action at most.
//
//   <PageHeader
//     title="Staff"
//     subtitle="Who can sign in to the CMS, and their role."
//     breadcrumbs={[{ label: "People" }, { label: "Staff" }]}
//     actions={<ActionButton variant="contained">Add a staff member</ActionButton>}
//   />
const PageHeader = ({ title, subtitle, breadcrumbs, actions, children, sx }) => (
  <Box component="header" sx={[{ mb: { xs: 2, sm: 3 } }, ...(Array.isArray(sx) ? sx : [sx])]}>
    {breadcrumbs?.length > 1 && (
      <Breadcrumbs
        aria-label="You are here"
        separator={<IconChevronRight size={14} aria-hidden />}
        sx={{ mb: 0.75, fontSize: "0.8125rem", color: "text.secondary", "& .MuiBreadcrumbs-separator": { mx: 0.5 } }}
      >
        {breadcrumbs.map((crumb, index) => {
          const last = index === breadcrumbs.length - 1;
          return crumb.to && !last ? (
            <Link key={crumb.label} component={RouterLink} to={crumb.to} underline="hover" color="inherit" sx={{ fontWeight: 500 }}>
              {crumb.label}
            </Link>
          ) : (
            <Typography
              key={crumb.label}
              component="span"
              aria-current={last ? "page" : undefined}
              sx={{ fontSize: "inherit", color: last ? "text.primary" : "inherit", fontWeight: last ? 600 : 500 }}
            >
              {crumb.label}
            </Typography>
          );
        })}
      </Breadcrumbs>
    )}
    <Stack
      direction={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "stretch", sm: "center" }}
      spacing={{ xs: 1.5, sm: 2 }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h3" component="h1">
          {title}
        </Typography>
        {subtitle && (
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
          {actions}
        </Stack>
      )}
    </Stack>
    {children}
  </Box>
);

PageHeader.propTypes = {
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  breadcrumbs: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string.isRequired, to: PropTypes.string })),
  actions: PropTypes.node,
  children: PropTypes.node,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.func, PropTypes.array]),
};

export default PageHeader;
